import { useAppDispatch, useAppSelector, useAppStore } from 'app/hooks';
import { boundedCodesFor } from 'components/equipment-page/ducted-facet-merge';
import {
  buildDefaultDuctedFetchParams,
  ductedCombinationsRequestHash,
  fetchDuctedCompatibleCombinations,
  summaryPending
} from 'features/ducted-compatibility';
import useIsCompatibilityPathway from 'hooks/use-is-compatibility-pathway';
import { useEffect, useMemo } from 'react';
import {
  ductedPairLoadsKey,
  selectDuctedPairContextBySystemId
} from 'utils/ducted-pair-context';
import {
  ductedSeedChassisSizes,
  ductedSeedOrientations,
  getChooseSystemsEligibleDuctedSystems
} from 'utils/existing-equipment';
import { buildComponentsKey } from 'utils/recommendations-strip-logic';

// Choose Systems pre-warm, mounted on the Existing Equipment step (AHA-6681
// sub-build 5, plan §6): dispatches the REAL Choose Systems thunk with the
// post-reset DEFAULT params (buildDefaultDuctedFetchParams) as soon as the
// wanted selections settle, so the pnet Redis/memo caches AND the Redux
// slice are warm before the user ever opens the page — the page and the
// rail's Recommended cards ride the same response. No new server job — a
// real user request warms everything in one motion.
//
// Cadence: no timer of its own. The wants gateway's bound comes from the
// LES-4 summary, which the step's 600ms-debounced runCompatibilityCheck
// lands — this hook keys on that summary (boundedCodesKey), so rapid want
// edits collapse through the EXISTING debounce before anything warms, and a
// fingerprint/componentsKey change re-fires through the same pipeline (the
// strip's overwrite path reseeds wants -> the debounced check re-lands the
// summary -> the keys below change).
//
// Dedupe/attach/supersede: params come from the shared builder, so the
// requestBodyHash is byte-identical to the page's own first fetch — an
// entry already warm (or warming) under the same hash is skipped here, a
// page arrival mid-warm attaches via the thunk's in-flight short-circuit,
// and a hash change (new bound/kept set) simply begins a newer fetch that
// the slice's latest-wins guards let supersede the stale warm.
//
// Purely additive: never records skips (the page hook owns the honest-skip
// bookkeeping), never touches the tab gating, and is harmless when the
// user never reaches Choose Systems.
export default function useDuctedPrewarm() {
  const dispatch = useAppDispatch();
  const store = useAppStore();
  const isDucted = useIsCompatibilityPathway();
  const existingSystems = useAppSelector(
    (state) => state.structure.existingSystems
  );
  const compatibleResultsBySystem = useAppSelector(
    (state) => state.structure.compatibleResultsBySystem
  );
  const recommendationSelectionsBySystem = useAppSelector(
    (state) => state.structure.recommendationSelectionsBySystem
  );
  const wantCompositionPicksBySystem = useAppSelector(
    (state) => state.structure.wantCompositionPicksBySystem
  );
  const recommendationSelectionsLoaded = useAppSelector(
    (state) => state.structure.recommendationSelectionsLoaded
  );
  const ductedFilters = useAppSelector((state) => state.ui.ductedFilters);
  const inStockOnly = useAppSelector(
    (state) => state.ui.galleryFilters.inStock
  );
  const priceMin = useAppSelector((state) => state.ui.galleryFilters.priceMin);
  const priceMax = useAppSelector((state) => state.ui.galleryFilters.priceMax);
  const locationZip = useAppSelector(
    (state) => state.project.locationData?.zipcode
  );
  const totalCoolingBTU = useAppSelector(
    (state) => state.project.totalBTUs?.cooling
  );
  const totalHeatingBTU = useAppSelector(
    (state) => state.project.totalBTUs?.heating
  );
  const pairLoadsKey = useAppSelector((state) =>
    ductedPairLoadsKey(selectDuctedPairContextBySystemId(state))
  );
  // The "Sort by" selection the default combinations body embeds (the
  // builder reads it from the same store slice), so the warm's hash matches
  // the page's first dispatch and a dropdown change re-warms.
  const systemSort = useAppSelector((state) => state.ui.systemSort);

  // Same eligibility as the page hook (virtual-tile haves included) so the
  // content keys below cover the zero-component systems the default params
  // builder now emits for.
  const eligibleSystems = useMemo(
    () =>
      isDucted
        ? getChooseSystemsEligibleDuctedSystems(
            existingSystems,
            recommendationSelectionsBySystem,
            wantCompositionPicksBySystem
          )
        : [],
    [
      isDucted,
      existingSystems,
      recommendationSelectionsBySystem,
      wantCompositionPicksBySystem
    ]
  );

  // Content-keyed effect deps, same pattern (and reasons) as
  // useDuctedCompatibilityFetch: structure/summary writes churn references
  // without changing the warm inputs, so the effects key on these strings.
  const componentsKey = useMemo(
    () =>
      eligibleSystems
        .map(
          (system) =>
            `${system.uniqueId}=${buildComponentsKey(system.components)}`
        )
        .join(';'),
    [eligibleSystems]
  );

  const wantsKey = useMemo(
    () =>
      eligibleSystems
        .map(
          (system) =>
            `${system.uniqueId}:${(
              recommendationSelectionsBySystem[system.uniqueId]?.wantIds ?? []
            ).join(',')}`
        )
        .join(';'),
    [eligibleSystems, recommendationSelectionsBySystem]
  );

  // The per-system recommended bound, pending-marked like the page hook's
  // key so a loading->settled transition of an unchanged code list still
  // refires (the settle is what arms the warm).
  const boundedCodesKey = useMemo(
    () =>
      eligibleSystems
        .map((system) => {
          const summary = compatibleResultsBySystem[system.uniqueId];
          const bounded = boundedCodesFor(summary);
          return `${system.uniqueId}:${
            bounded === null ? '*' : bounded.join(',')
          }:${summaryPending(summary) ? 'pending' : 'settled'}`;
        })
        .join(';'),
    [eligibleSystems, compatibleResultsBySystem]
  );

  // Content key for the chassis-seed inputs (AHA-6796): cabinetChassisSize
  // is a SPEC field NOT encoded in componentsKey (uniqueId:replacing only),
  // so a spec-only chassis fill changes the default request hash without
  // moving any other dep — buildDefaultDuctedFetchParams derives the seeded
  // filter fresh from state, so only the effect TRIGGER is missing without
  // this key. Derived through the same helper as the builder so the key
  // and the derivation cannot drift.
  const chassisSeedKey = useMemo(
    () =>
      ductedSeedChassisSizes(
        eligibleSystems,
        wantCompositionPicksBySystem
      ).join(','),
    [eligibleSystems, wantCompositionPicksBySystem]
  );

  // Same for the orientation seed: a spec-only orientation fill changes the
  // default request hash without moving any other dep.
  const orientationSeedKey = useMemo(
    () => ductedSeedOrientations(eligibleSystems).join(','),
    [eligibleSystems]
  );

  // Combinations warm: one dispatch per (eligible system, resultSet) whose
  // default-request hash is not already warm/warming in the slice.
  useEffect(() => {
    // Hard gate: zero effect work — and zero fetches — outside the ducted
    // pathway or before the wants hydration lands.
    if (!isDucted || eligibleSystems.length === 0) return;
    if (!recommendationSelectionsLoaded) return;

    const state = store.getState();
    buildDefaultDuctedFetchParams(state).forEach((params) => {
      const requestBodyHash = ductedCombinationsRequestHash(params);
      const existing =
        state.ductedCompatibility.resultsBySystem[params.systemUniqueId]?.[
          params.resultSet
        ];
      // Already warm (loaded/skipped) or warming (in flight) under the
      // identical hash: nothing to do. An 'error' entry may retry when the
      // inputs change.
      if (
        existing &&
        existing.requestBodyHash === requestBodyHash &&
        existing.status !== 'error'
      ) {
        return;
      }
      dispatch(fetchDuctedCompatibleCombinations(params));
    });
    // The store snapshot is read inside the effect; the content-keyed
    // strings above (plus the filter/toggle values the default hash embeds)
    // are the complete set of warm inputs — chassisSeedKey and
    // orientationSeedKey cover the spec-derived seeds that componentsKey
    // cannot see (AHA-6796), and the fuel selection + source flags cover
    // the manual-preserved composition (resolveContext*) the default params
    // now embed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isDucted,
    componentsKey,
    wantsKey,
    boundedCodesKey,
    chassisSeedKey,
    orientationSeedKey,
    recommendationSelectionsLoaded,
    ductedFilters.filters,
    ductedFilters.fuelSelections,
    ductedFilters.fuelSelectionsSource,
    ductedFilters.chassisFilterSource,
    ductedFilters.orientationFilterSource,
    ductedFilters.pageSize,
    ductedFilters.tonnageSeedKey,
    inStockOnly,
    priceMin,
    priceMax,
    locationZip,
    totalCoolingBTU,
    totalHeatingBTU,
    pairLoadsKey,
    systemSort,
    dispatch,
    store
  ]);
}
